import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, CardContent, Chip, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import {
  fetchRequest as fetchDriversNow,
  clearState as clearDriversNow,
} from '../../redux/actions/discovery/driversNow';
import { useDisplayNames } from '../../utils/displayNames';
import EdgeDrawer from './EdgeDrawer';

/* Each driver carries two independent belief axes:
   - confidence (mu): how sure we are the relationship is real (regularizes).
   - impact: how large the effect is (prioritizes & explains).
   We plot impact (x) against confidence (y) so the top-right is "trust it AND
   it moves the needle". Tier colours come from the discovery lifecycle. */
const TIER_META = {
  validated: { label: 'Validated', color: '#2e7d32' },
  suggestive: { label: 'Suggestive', color: '#ed6c02' },
  candidate: { label: 'Candidate', color: '#0288d1' },
  leading: { label: 'Leading indicator', color: '#7b1fa2' },
};

const tierMeta = (tier) => TIER_META[tier] || TIER_META.candidate;

const clamp01 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
};

const PredictiveInfluencePanel = ({ goalId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const nameOf = useDisplayNames();
  const [hovered, setHovered] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const data = useSelector((s) => s.discovery_drivers_now?.response);

  useEffect(() => {
    if (goalId) dispatch(fetchDriversNow({ goalId }));
    return () => { dispatch(clearDriversNow()); };
  }, [goalId, dispatch]);

  const points = useMemo(() => {
    const validated = Array.isArray(data?.drivers) ? data.drivers : [];
    const predictive = Array.isArray(data?.predictive_drivers)
      ? data.predictive_drivers : [];
    const merged = [...validated, ...predictive]
      // scenario_only twins are base-signal re-keyings of an engineered
      // feature's edge for the Scenarios picker — the evidence dot here
      // stays on the feature the edge was actually measured on.
      .filter((d) => d && d.key && !d.scenario_only)
      .map((d, i) => ({
        id: d.edge_id || `${d.key}-${d.channel || 'level'}-${i}`,
        key: d.key,
        kind: d.kind,
        channel: d.channel,
        tier: d.tier || 'validated',
        direction: d.direction || 'positive',
        confidence: clamp01(d.confidence),
        impact: clamp01(d.impact),
        leading: Boolean(d.leading_indicator),
      }));
    // Stable ordering so labels and points line up; strongest first.
    merged.sort((a, b) => (b.confidence * b.impact) - (a.confidence * a.impact));
    return merged;
  }, [data]);

  const tierCounts = useMemo(() => {
    const counts = {};
    points.forEach((p) => { counts[p.tier] = (counts[p.tier] || 0) + 1; });
    return counts;
  }, [points]);

  const visible = useMemo(
    () => (tierFilter ? points.filter((p) => p.tier === tierFilter) : points),
    [points, tierFilter],
  );

  if (!points.length) return null;

  // SVG plot geometry — larger, centered. Padding leaves room for axis ticks
  // and titles; the plot square keeps confidence/impact visually comparable.
  const W = 720;
  const H = 460;
  const pad = { l: 60, r: 28, t: 30, b: 52 };
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const sx = (impact) => pad.l + impact * plotW;
  const sy = (conf) => pad.t + (1 - conf) * plotH;
  const midX = sx(0.5);
  const midY = sy(0.5);

  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  const active = hovered || (selectedEdge
    ? (visible.find((p) => p.edge_id === selectedEdge) || {}).id : null);

  // Name labels only on the meaningful points (or the focused one) so the
  // dense low-confidence blob stays clean while the drivers that matter read
  // themselves off the plot. Impact 0.5 is just the correlation cap where the
  // candidate cluster piles up, so require a touch more before auto-labelling.
  const labelled = (p) => p.confidence >= 0.5 || p.impact >= 0.55
    || active === p.id;

  // Collision-avoidance: place labels one at a time (most important first, the
  // focused one always), nudging vertically into the first free slot. Anything
  // that still can't fit is dropped — the dot stays and hover reveals it — so
  // labels never stack on top of each other.
  const labelLayout = (() => {
    const CH = 5.6;   // approx label char width at 10px
    const LH = 13;    // line height / vertical step
    const placed = [];
    const out = [];
    const hit = (a, b) => !(a.x2 < b.x1 || a.x1 > b.x2
      || a.y2 < b.y1 || a.y1 > b.y2);
    const ranked = visible.filter(labelled).slice().sort((a, b) => {
      if (active === a.id) return -1;
      if (active === b.id) return 1;
      return (b.confidence * b.impact) - (a.confidence * a.impact);
    });
    ranked.forEach((p) => {
      const cx = sx(p.impact);
      const cy = sy(p.confidence);
      const focused = active === p.id;
      const text = nameOf(p.key)
        + (focused ? `  ·  ${(p.confidence * 100).toFixed(0)}% / ${(p.impact * 100).toFixed(0)}%` : '');
      const w = text.length * CH + 12;
      const rightHalf = cx > pad.l + plotW * 0.62;
      const baseX = rightHalf ? cx - 10 : cx + 10;
      const x1 = rightHalf ? baseX - w : baseX;
      const x2 = rightHalf ? baseX : baseX + w;
      const steps = [0, -LH, LH, -2 * LH, 2 * LH, -3 * LH, 3 * LH];
      let fy = null;
      for (let i = 0; i < steps.length; i += 1) {
        const yy = cy + steps[i];
        if (yy < pad.t + 6 || yy > pad.t + plotH - 4) continue;
        const box = { x1, x2, y1: yy - 9, y2: yy + 4 };
        if (!placed.some((b) => hit(box, b))) { fy = yy; placed.push(box); break; }
      }
      if (fy === null && focused) { fy = cy; placed.push({ x1, x2, y1: cy - 9, y2: cy + 4 }); }
      if (fy === null) return;
      out.push({
        p, text, x: baseX, y: fy + 3.5, anchor: rightHalf ? 'end' : 'start',
        cx, cy, nudged: Math.abs(fy - cy) > 1, focused,
      });
    });
    return out;
  })();

  // Corner labels give each quadrant meaning without a paragraph of prose.
  const quadrants = [
    { x: midX + 6, y: pad.t + 13, anchor: 'start', text: 'TRUST + IMPACT' },
    { x: midX - 6, y: pad.t + 13, anchor: 'end', text: 'TRUST · LOW IMPACT' },
    { x: midX + 6, y: pad.t + plotH - 7, anchor: 'start', text: 'IMPACT · LOW TRUST' },
    { x: midX - 6, y: pad.t + plotH - 7, anchor: 'end', text: 'WEAK' },
  ];

  const openEdge = (p) => { if (p?.edge_id) setSelectedEdge(p.edge_id); };

  return (
    <>
      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <InsightsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">
              Predictive influence — confidence vs impact ({points.length})
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Every signal the model can learn from, not just the validated ones.
            Vertical = how sure we are it&apos;s real (confidence). Horizontal =
            how much it moves the target (impact). Top-right matters most.
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {Object.keys(TIER_META).map((tier) => {
              const meta = tierMeta(tier);
              const count = tierCounts[tier] || 0;
              if (!count) return null;
              const on = tierFilter === tier;
              return (
                <Chip
                  key={tier}
                  size="small"
                  variant={on ? 'filled' : 'outlined'}
                  onClick={() => setTierFilter(on ? null : tier)}
                  label={`${meta.label} · ${count}`}
                  sx={{
                    cursor: 'pointer',
                    borderColor: meta.color,
                    color: on ? '#fff' : meta.color,
                    bgcolor: on ? meta.color : 'transparent',
                    '& .MuiChip-label': { fontWeight: 600, fontSize: 11 },
                    '&:hover': { bgcolor: on ? meta.color : alpha(meta.color, 0.1) },
                  }}
                />
              );
            })}
          </Stack>

          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              width="100%"
              style={{ maxWidth: W, display: 'block' }}
              onMouseLeave={() => setHovered(null)}
            >
              {/* "Matters most" quadrant — subtle wash in the top-right. */}
              <rect
                x={midX} y={pad.t} width={pad.l + plotW - midX} height={midY - pad.t}
                fill={alpha(theme.palette.success.main, 0.07)}
              />
              {/* Grid + tick labels */}
              {gridLines.map((g) => (
                <g key={`gx-${g}`}>
                  <line
                    x1={sx(g)} y1={pad.t} x2={sx(g)} y2={pad.t + plotH}
                    stroke={theme.palette.divider} strokeWidth={g === 0 ? 1 : 0.5}
                  />
                  <text
                    x={sx(g)} y={pad.t + plotH + 18} textAnchor="middle"
                    fontSize="10" fill={theme.palette.text.secondary}
                  >
                    {g}
                  </text>
                </g>
              ))}
              {gridLines.map((g) => (
                <g key={`gy-${g}`}>
                  <line
                    x1={pad.l} y1={sy(g)} x2={pad.l + plotW} y2={sy(g)}
                    stroke={theme.palette.divider} strokeWidth={g === 0 ? 1 : 0.5}
                  />
                  <text
                    x={pad.l - 8} y={sy(g) + 3} textAnchor="end"
                    fontSize="10" fill={theme.palette.text.secondary}
                  >
                    {g}
                  </text>
                </g>
              ))}
              {/* Quadrant midlines + corner labels */}
              <line
                x1={midX} y1={pad.t} x2={midX} y2={pad.t + plotH}
                stroke={theme.palette.text.secondary} strokeWidth={1}
                strokeDasharray="4 4" opacity={0.4}
              />
              <line
                x1={pad.l} y1={midY} x2={pad.l + plotW} y2={midY}
                stroke={theme.palette.text.secondary} strokeWidth={1}
                strokeDasharray="4 4" opacity={0.4}
              />
              {quadrants.map((q) => (
                <text
                  key={q.text}
                  x={q.x} y={q.y} textAnchor={q.anchor}
                  fontSize="9" fontWeight="700" letterSpacing="0.5"
                  fill={theme.palette.text.secondary} opacity={0.55}
                >
                  {q.text}
                </text>
              ))}
              {/* Axis titles */}
              <text
                x={pad.l + plotW / 2} y={H - 6} textAnchor="middle"
                fontSize="11" fontWeight="600" fill={theme.palette.text.secondary}
              >
                Impact →
              </text>
              <text
                x={16} y={pad.t + plotH / 2} textAnchor="middle"
                fontSize="11" fontWeight="600" fill={theme.palette.text.secondary}
                transform={`rotate(-90 16 ${pad.t + plotH / 2})`}
              >
                Confidence →
              </text>

              {/* Focus crosshair for the active point */}
              {active && (() => {
                const p = visible.find((v) => v.id === active);
                if (!p) return null;
                const cx = sx(p.impact);
                const cy = sy(p.confidence);
                return (
                  <g pointerEvents="none">
                    <line x1={pad.l} y1={cy} x2={cx} y2={cy}
                      stroke={tierMeta(p.tier).color} strokeWidth={1}
                      strokeDasharray="3 3" opacity={0.6} />
                    <line x1={cx} y1={pad.t + plotH} x2={cx} y2={cy}
                      stroke={tierMeta(p.tier).color} strokeWidth={1}
                      strokeDasharray="3 3" opacity={0.6} />
                  </g>
                );
              })()}

              {/* Points (dimmed when another point is focused) */}
              {visible.map((p) => {
                const meta = tierMeta(p.tier);
                const isActive = active === p.id;
                const dim = active && !isActive ? 0.28 : 1;
                return (
                  <circle
                    key={p.id}
                    cx={sx(p.impact)}
                    cy={sy(p.confidence)}
                    r={isActive ? 8 : 5}
                    fill={alpha(meta.color, 0.75)}
                    stroke={meta.color}
                    strokeWidth={isActive ? 2.5 : 1}
                    opacity={dim}
                    style={{ cursor: 'pointer', transition: 'r 90ms ease, opacity 90ms ease' }}
                    onMouseEnter={() => setHovered(p.id)}
                    onClick={() => openEdge(p)}
                  >
                    <title>
                      {`${nameOf(p.key)}\nconfidence ${(p.confidence * 100).toFixed(0)}% · impact ${(p.impact * 100).toFixed(0)}%\n${meta.label} — click for evidence`}
                    </title>
                  </circle>
                );
              })}

              {/* Name labels (collision-avoided) for meaningful / focused
                  points. A thin leader connects any label nudged off its dot. */}
              {labelLayout.map((l) => (
                <g key={`lbl-${l.p.id}`} pointerEvents="none"
                  style={{ transition: 'opacity 90ms ease' }}
                  opacity={active && !l.focused ? 0.4 : 0.95}
                >
                  {l.nudged && (
                    <line
                      x1={l.cx} y1={l.cy}
                      x2={l.anchor === 'end' ? l.x + 2 : l.x - 2} y2={l.y - 3.5}
                      stroke={theme.palette.text.secondary}
                      strokeWidth={0.5} opacity={0.4}
                    />
                  )}
                  <text
                    x={l.x} y={l.y} textAnchor={l.anchor}
                    fontSize={l.focused ? 11 : 10}
                    fontWeight={l.focused ? 700 : 500}
                    fill={l.focused ? tierMeta(l.p.tier).color : theme.palette.text.primary}
                  >
                    {l.text}
                  </text>
                </g>
              ))}
            </svg>
          </Box>

          {/* Top movers list — ranked by confidence × impact, click for evidence */}
          <Stack sx={{ mt: 1.25 }}>
            {visible.slice(0, 8).map((p) => {
              const meta = tierMeta(p.tier);
              const DirIcon = p.direction === 'negative' ? TrendingDownIcon : TrendingUpIcon;
              const isActive = active === p.id;
              return (
                <Stack
                  key={`row-${p.id}`}
                  direction="row"
                  spacing={0.9}
                  alignItems="center"
                  onMouseEnter={() => setHovered(p.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => openEdge(p)}
                  sx={{
                    px: 0.75, py: 0.3, borderRadius: 0.75, cursor: 'pointer',
                    bgcolor: isActive ? 'action.hover' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    bgcolor: meta.color,
                  }}
                  />
                  <DirIcon sx={{
                    fontSize: 14, flexShrink: 0,
                    color: p.direction === 'negative' ? 'error.main' : 'success.main',
                  }}
                  />
                  <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0, fontWeight: 500, fontSize: 13 }} title={p.key}>
                    {nameOf(p.key)}
                    {p.leading && (
                      <Tooltip title="Fresher upstream signal injected as a low-confidence leading indicator" arrow>
                        <Chip size="small" label="leading" sx={{ ml: 0.75, height: 15, '& .MuiChip-label': { px: 0.75, fontSize: 9 } }} />
                      </Tooltip>
                    )}
                  </Typography>
                  {/* Confidence mini-bar */}
                  <Tooltip title={`Confidence ${(p.confidence * 100).toFixed(0)}%`} arrow>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 84, flexShrink: 0 }}>
                      <Box sx={{ flexGrow: 1, height: 4, borderRadius: 2, bgcolor: alpha(meta.color, 0.15), overflow: 'hidden' }}>
                        <Box sx={{ width: `${p.confidence * 100}%`, height: '100%', bgcolor: meta.color }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, width: 28, textAlign: 'right', fontSize: 11 }}>
                        {(p.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Stack>
                  </Tooltip>
                  {/* Impact mini-bar */}
                  <Tooltip title={`Impact ${(p.impact * 100).toFixed(0)}%`} arrow>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 84, flexShrink: 0 }}>
                      <Box sx={{ flexGrow: 1, height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.text.secondary, 0.15), overflow: 'hidden' }}>
                        <Box sx={{ width: `${p.impact * 100}%`, height: '100%', bgcolor: theme.palette.text.secondary }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 28, textAlign: 'right', fontSize: 11 }}>
                        {(p.impact * 100).toFixed(0)}%
                      </Typography>
                    </Stack>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <EdgeDrawer edgeId={selectedEdge} onClose={() => setSelectedEdge(null)} />
    </>
  );
};

export default PredictiveInfluencePanel;
